<script setup lang="ts">
import type { Entitlement, MonospacePolicyEntitlementResource, PolicyEntitlementAction } from '#layers/access/types/permissions/policy';
import { useOverlay } from '@nuxt/ui/composables';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePolicyEntitlementTranslations } from '#layers/access/composables/policy/use-policy-entitlement-translations';
import { usePolicyEntitlements } from '#layers/access/composables/policy/use-policy-entitlements';
import { showConfirmationDialog } from '#layers/ui/public';
import PolicyEntitlementDrawer from './PolicyEntitlementDrawer.vue';

interface PolicyEntitlementResourceActionProps {
	resource: MonospacePolicyEntitlementResource;
	action: PolicyEntitlementAction;
}

const { resource, action } = defineProps<PolicyEntitlementResourceActionProps>();
const modelValue = defineModel<Entitlement[]>({ required: true });
const { t } = useI18n();

const { hasEntitlement, removeEntitlement, addEntitlementWildcard, selectedResourceIdsForEntitlement, hasWildcardEntitlement, setEntitlements } = usePolicyEntitlements({ entitlements: modelValue });

const computedHasEntitlement = computed({
	get() {
		return hasEntitlement(resource, action);
	},
	set(value) {
		if (value) {
			addEntitlementWildcard(resource, action);
		}
		else {
			if (hasWildcardEntitlement(resource, action) || !action.hasIdentifier) {
				removeEntitlement(resource, action);
			}
			else {
				confirmRemoveEntitlement();
			}
		}
	},
});

const hasResourceSelection = computed(() => action.hasIdentifier
	&& (hasWildcardEntitlement(resource, action) || selectedResourceIdsForEntitlement(resource, action).length > 0));

const buttonLabel = computed(() => {
	const count = selectedResourceIdsForEntitlement(resource, action).length;

	if (hasWildcardEntitlement(resource, action)) {
		return t('common.all_things', { things: t(`policy.entitlements.${resource.name}.title`, { count: 2 }) });
	}

	return `${count} ${t(`policy.entitlements.${resource.name}.title`, { count })}`;
});

const { entitlementName, entitlementDescription, resourceTitlePlural } = usePolicyEntitlementTranslations({
	resource: () => resource,
	action: () => action,
});

async function handleAddEntitlement() {
	if (!action.hasIdentifier || !resource.collectionId) {
		return;
	}

	const modal = useOverlay().create(PolicyEntitlementDrawer, {
		props: {
			collectionId: resource.collectionId,
			entitlements: modelValue.value,
			resource,
			action,
		},
	});
	const instance = modal.open();
	const payload = await instance.result;

	if (payload?.data) {
		setEntitlements(payload.data.entitlements, resource, action);
	}
}

async function confirmRemoveEntitlement() {
	const count = selectedResourceIdsForEntitlement(resource, action).length;

	showConfirmationDialog({
		props: {
			title: t('policy.entitlements.confirm_remove_entitlement_title'),
			description: t('policy.entitlements.confirm_remove_entitlement_description', { count, resources: resourceTitlePlural.value }),
			confirm: { color: 'warning' },
		},
		onConfirm: () => {
			removeEntitlement(resource, action);
		},
	});
}
</script>

<template>
	<div class="flex items-center gap-2">
		<DSwitch
			:id="`${resource.name}.${action.name}`"
			v-model="computedHasEntitlement"
			size="sm"
			class="items-center"
			:label="entitlementName"
			:ui="{ label: 'text-sm' }"
		/>
		<div class="flex flex-1 items-center gap-2">
			<DTooltip v-if="entitlementDescription" :text="entitlementDescription">
				<DIcon name="lucide:info" class="text-dimmed" />
			</DTooltip>
			<DTooltip
				v-if="hasResourceSelection"
				:text="$t(`common.select_thing`, { thing: resourceTitlePlural })"
			>
				<DButton
					:data-testid="`entitlement-selection-${resource.name}-${action.name}`"
					size="xs"
					variant="soft"
					color="primary"
					:label="buttonLabel"
					@click="handleAddEntitlement"
				/>
			</DTooltip>
		</div>
	</div>
</template>
